import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { ArrowRight, ChevronRight, CheckCircle2, Check, Calendar, Beaker, FileText, HeartPulse, Dna, Activity, Stethoscope, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import heroBg from '../assets/hero_blood.png';

export default function Home() {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="w-full">
      {/* HERO SECTION */}
      <section className="relative w-full min-h-[600px] md:h-[85vh] py-28 md:py-0 flex items-center justify-center bg-slate-900 overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute inset-0 z-0">
           {/* Degradê Cinza escuro para contraste do texto */}
           <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent z-10" />
           <div className="absolute right-0 top-1/4 w-[800px] h-[800px] bg-slate-400 opacity-10 rounded-full blur-[120px] mix-blend-screen z-10" />
           {/* Imagem de Fundo c/ mix-blend */}
           <img 
             src={heroBg} 
             alt="Coleta de Sangue Laboratório" 
             className="absolute inset-0 w-full h-full object-cover opacity-50 mix-blend-luminosity"
           />
        </div>

        <div className="container relative z-20 mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center">
          <motion.div 
            className="w-full md:w-3/5 text-white"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>Acreditação DICQ – Excelência Comprovada</span>
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-[1.1]">
              Medicina <span className="text-santa-green font-light">Laboratorial</span><br/> de vanguarda.
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
              O primeiro Laboratório a ser aprovado pelo sistema nacional de acreditação em Três Rios. Mais de meio século como referência em qualidade.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="mr-0 sm:mr-4" href="https://santaizabel.shiftcloud.com.br/shift/lis/santaizabel/elis/s01.iu.web.Login.cls" target="_blank" rel="noopener noreferrer">
                Resultados Online
              </Button>
              <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10" href="https://api.whatsapp.com/send?phone=5524988250515" target="_blank" rel="noopener noreferrer">
                Agendar Coleta
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* QUICK ACTIONS BAR - FLOATING */}
      <section className="relative z-30 -mt-8 md:-mt-16 container mx-auto px-4 md:px-8 mb-24">
        <div className="bg-gradient-to-r from-santa-green to-emerald-600 rounded-[2rem] shadow-[0_20px_40px_-15px_rgba(34,197,94,0.3)] p-4 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-white/20 border border-white/20">
           
           {/* Item 1: Resultados */}
           <a href="https://santaizabel.shiftcloud.com.br/shift/lis/santaizabel/elis/s01.iu.web.Login.cls" target="_blank" rel="noopener noreferrer" className="flex items-start gap-5 p-4 hover:bg-white/10 rounded-2xl transition-all duration-300 cursor-pointer group">
             <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-full flex items-center justify-center text-white group-hover:bg-white group-hover:text-santa-green transition-all duration-300 shrink-0">
               <FileText className="w-6 h-6 stroke-[1.5]" />
             </div>
             <div>
               <h3 className="font-bold text-white text-lg mb-1 transition-colors">Resultados</h3>
               <p className="text-sm text-emerald-50 mb-3 leading-relaxed">Acesse seus laudos com total segurança e rapidez.</p>
               <span className="text-white text-sm font-semibold flex items-center gap-1 group-hover:translate-x-1 transition-all duration-300">
                 Acessar Agora <ChevronRight className="w-4 h-4" />
               </span>
             </div>
           </a>

           {/* Item 2: Agendamento */}
           <a href="https://api.whatsapp.com/send?phone=5524988250515" target="_blank" rel="noopener noreferrer" className="flex items-start gap-5 p-4 hover:bg-white/10 rounded-2xl transition-all duration-300 cursor-pointer group">
             <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-full flex items-center justify-center text-white group-hover:bg-white group-hover:text-santa-green transition-all duration-300 shrink-0">
               <Calendar className="w-6 h-6 stroke-[1.5]" />
             </div>
             <div>
               <h3 className="font-bold text-white text-lg mb-1 transition-colors">Agendamento</h3>
               <p className="text-sm text-emerald-50 mb-3 leading-relaxed">Agende sua coleta rapidamente via WhatsApp.</p>
               <span className="text-white text-sm font-semibold flex items-center gap-1 group-hover:translate-x-1 transition-all duration-300">
                 Falar com Atendente <ChevronRight className="w-4 h-4" />
               </span>
             </div>
           </a>

           {/* Item 3: Contato */}
           <Link to="/unidades" className="flex items-start gap-5 p-4 hover:bg-white/10 rounded-2xl transition-all duration-300 cursor-pointer group">
             <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-full flex items-center justify-center text-white group-hover:bg-white group-hover:text-santa-green transition-all duration-300 shrink-0">
               <MapPin className="w-6 h-6 stroke-[1.5]" />
             </div>
             <div>
               <h3 className="font-bold text-white text-lg mb-1 transition-colors">Unidades e Contato</h3>
               <p className="text-sm text-emerald-50 mb-3 leading-relaxed">Veja os endereços ou tire dúvidas com a equipe.</p>
               <span className="text-white text-sm font-semibold flex items-center gap-1 group-hover:translate-x-1 transition-all duration-300">
                 Ver Informações <ChevronRight className="w-4 h-4" />
               </span>
             </div>
           </Link>

        </div>
      </section>

      {/* INSTITUTIONAL HIGHLIGHT */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center gap-16">
          <div className="w-full md:w-1/2">
            <h2 className="text-4xl font-bold text-santa-dark mb-6 tracking-tight">Referência de qualidade <br/>há mais de meio século.</h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              O Laboratório Santa Izabel Medicina Laboratorial é administrado pelo Dr. Norival Rodrigues e a Dra. Maria Inês Rocha Costa. Visando um bom atendimento e focando na saúde dos nossos clientes, trabalhamos sempre com novas tecnologias e insumos de qualidade reconhecida.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white shadow-sm border border-slate-100 rounded-full flex items-center justify-center text-santa-green">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <span className="font-medium text-slate-700">Acreditado DICQ</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white shadow-sm border border-slate-100 rounded-full flex items-center justify-center text-santa-green">
                  <Stethoscope className="w-6 h-6" />
                </div>
                <span className="font-medium text-slate-700">Equipe de Excelência</span>
              </div>
            </div>
            <Link to="/sobre">
              <Button variant="outline" className="group">
                Conheça a nossa história
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          <div className="w-full md:w-1/2 relative">
             <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-premium">
               <img src="/recepcao.jpg" alt="Recepção do Laboratório Santa Izabel" className="w-full h-full object-cover" />
             </div>
             <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-xl shadow-premium border border-slate-100 w-64 hidden sm:block">
               <div className="text-4xl font-bold text-santa-green mb-1">50+</div>
               <div className="text-sm text-slate-500 font-medium tracking-wide uppercase">Anos de Tradição</div>
             </div>
          </div>
        </div>
      </section>

      {/* EXAMS MENU - PREMIUM CARDS */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        {/* Soft abstract shape glows to make it extremely premium */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-santa-green/10 rounded-full blur-[120px] pointer-events-none -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[150px] pointer-events-none translate-y-1/4 -translate-x-1/4" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-white mb-4 tracking-tight font-outfit">Nossas Especialidades</h2>
            <p className="text-lg text-slate-300">Realizamos ampla variedade de exames com precisão e tecnologia de ponta para atender as necessidades de clientes e médicos.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
             {[
              { 
                title: 'Biologia Molecular', 
                icon: Dna,
                desc: 'Análises modernas de DNA/RNA com altíssima exatidão diagnóstica.' 
              },
              { 
                title: 'Bioquímica', 
                icon: Beaker,
                desc: 'Medições precisas de vitaminas, glicose e marcadores orgânicos.'
              },
              { 
                title: 'Citopatologia', 
                icon: Activity,
                desc: 'Diagnósticos preventivos avançados com foco celular.'
              },
              { 
                title: 'Hematologia', 
                icon: HeartPulse,
                desc: 'Avaliação profunda e contagem de alta precisão do sangue.'
              },
            ].map((exam, idx) => (
              <Link to="/exames" key={idx} className="relative group flex flex-col p-8 rounded-[2rem] bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_20px_40px_-15px_rgba(34,197,94,0.4)] hover:-translate-y-2 transition-all duration-500 overflow-hidden isolate">
                
                {/* Hover Background Glow inside card */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-santa-green/10 to-transparent rounded-bl-[100px] -mr-8 -mt-8 transition-transform duration-700 group-hover:scale-[2] -z-10" />
                
                {/* Decorative bottom line */}
                <div className="absolute bottom-0 left-0 w-full h-1.5 bg-gradient-to-r from-santa-green to-emerald-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100 border border-green-200/50 flex items-center justify-center text-santa-green mb-6 group-hover:from-santa-green group-hover:to-emerald-600 group-hover:text-white group-hover:shadow-lg group-hover:shadow-santa-green/30 transform group-hover:-rotate-3 transition-all duration-500">
                  <exam.icon className="w-7 h-7 stroke-[1.5]" />
                </div>
                
                <h3 className="font-bold text-slate-800 text-xl group-hover:text-santa-green transition-colors mb-2">{exam.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{exam.desc}</p>
                
                <div className="mt-8 flex items-center text-sm font-semibold text-slate-400 group-hover:text-santa-dark transition-colors">
                  Saiba mais <ChevronRight className="w-4 h-4 ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/exames">
              <Button variant="ghost" className="text-white hover:text-santa-green hover:bg-white/10 font-medium">
                Ver todos os exames <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CONVÊNIOS PREMIUM MARQUEE */}
      <section className="py-24 bg-white relative overflow-hidden border-y border-slate-100">
        {/* Subtle background glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,197,94,0.03)_0%,transparent_70%)]" />

        <div className="container mx-auto px-4 md:px-8 relative z-10 mb-12">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-santa-dark mb-4 tracking-tight">Convênios Atendidos</h2>
            <p className="text-slate-500">Ampla rede de parceiros para facilitar o seu acesso à melhor medicina diagnóstica.</p>
          </div>
        </div>
        
        {/* Infinite Scroll Container */}
        <div className="relative w-full overflow-hidden flex flex-col items-center">
          
          {/* Edge fade masks */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-20 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-20 pointer-events-none" />

          {/* Marquee Track */}
          <motion.div 
            className="flex w-max gap-8 md:gap-16 items-center px-8"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ ease: "linear", duration: 40, repeat: Infinity }}
          >
             {/* Multiplicado 4 vezes para evitar espaços em branco em monitores ultra-wide (1920px, 4k, etc) */}
             {[
               ...Array(4).fill([
                 { name: 'GEAP Saúde', logo: '/assets/convenios/geap.png' },
                 { name: 'SEGTRAB MED', logo: '/assets/convenios/segtrab.png' },
                 { name: 'Faria Schmitz', logo: '/assets/convenios/faria-schmitz.png' },
                 { name: 'Bradesco', logo: '/assets/convenios/bradesco.png' },
                 { name: 'Amil', logo: '/assets/convenios/amil.png' },
                 { name: 'Saúde CAIXA', logo: '/assets/convenios/saude-caixa.png' },
                 { name: 'Cartão Santa Izabel Viver+', logo: '/assets/convenios/viver-mais.png', label: 'Cartão Viver+' },
                 { name: 'Sabin Sinai', logo: '/assets/convenios/sabin-sinai.png' },
               ]).flat()
             ].map((partner, idx) => (
                <div key={idx} className="flex-shrink-0 w-48 h-24 bg-white rounded-2xl border border-slate-200/80 shadow-[0_4px_20px_rgb(0,0,0,0.02)] flex flex-col items-center justify-center gap-1 p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 hover:border-santa-green/30 transition-all duration-300 group cursor-pointer">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-w-full flex-1 min-h-0 object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                  />
                  {'label' in partner && partner.label && (
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 group-hover:text-santa-green transition-colors">
                      {partner.label}
                    </span>
                  )}
                </div>
             ))}
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-santa-dark text-white overflow-hidden relative">
        <div className="absolute right-0 top-0 w-1/2 h-full bg-santa-green/5 blur-[120px] rounded-full mix-blend-screen pointer-events-none" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row gap-16 items-center">
             <div className="w-full md:w-1/3">
               <h2 className="text-3xl font-bold mb-6">O que as pessoas dizem</h2>
               <p className="text-slate-400 mb-8">Nossa excelência técnica se reflete no atendimento humano e dedicado a cada cliente.</p>
               <div className="flex gap-2">
                 <button className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-white/10 transition-colors">
                   <ChevronRight className="w-5 h-5 rotate-180" />
                 </button>
                 <button className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-white/10 transition-colors">
                   <ChevronRight className="w-5 h-5" />
                 </button>
               </div>
             </div>
             
             <div className="w-full md:w-2/3 flex gap-6 overflow-x-auto pb-8 snap-x hide-scrollbar">
                <div className="min-w-[320px] sm:min-w-[400px] snap-center bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <div className="flex text-amber-400 mb-6">★★★★★</div>
                  <p className="text-lg text-slate-200 mb-6 font-light italic">"Excelente atendimento, foram super atenciosos comigo e meu pai. Agradeço ao técnico de radiologia e recepcionista por toda atenção e paciência. Muitíssimo obrigada."</p>
                  <div className="font-medium text-white">— Juliana Lisboa</div>
                </div>

                <div className="min-w-[320px] sm:min-w-[400px] snap-center bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <div className="flex text-amber-400 mb-6">★★★★★</div>
                  <p className="text-lg text-slate-200 mb-6 font-light italic">"Espetacular atendimento, desde a chegada até o exame e entrega de resultado... A clínica como um todo superou as expectativas."</p>
                  <div className="font-medium text-white">— Anita Soares</div>
                </div>
                
                <div className="min-w-[320px] sm:min-w-[400px] snap-center bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <div className="flex text-amber-400 mb-6">★★★★★</div>
                  <p className="text-lg text-slate-200 mb-6 font-light italic">"Sempre fui bem atendida,os resultados saem super rápido, e tem um bom desconto com o cartão deles."</p>
                  <div className="font-medium text-white">— Mari Araujo</div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* VIVER MAIS PROMO */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
           <div className="bg-gradient-to-br from-slate-900 to-santa-dark rounded-[2rem] p-8 md:p-16 flex flex-col md:flex-row items-center justify-between shadow-premium-hover relative overflow-hidden">
              <div className="absolute right-0 bottom-0 w-[500px] h-[500px] bg-santa-gold/10 blur-[100px] rounded-full pointer-events-none" />
              
              <div className="w-full md:w-1/2 relative z-10 text-white text-center md:text-left mb-10 md:mb-0">
                 <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-santa-gold/30 bg-santa-gold/10 text-santa-gold mb-5 text-xs font-semibold uppercase tracking-widest">
                   Programa de Benefícios
                 </div>
                 <h2 className="text-4xl font-bold mb-4 font-outfit tracking-tight">Cartão <span className="text-santa-gold">Viver+</span></h2>
                 <p className="text-lg text-slate-300 mb-6 max-w-md mx-auto md:mx-0">Tenha benefícios exclusivos de verdade e ótimos descontos na realização dos seus exames laboratoriais.</p>

                 <ul className="space-y-3 mb-6 max-w-md mx-auto md:mx-0 text-left">
                   {[
                     'Até 60% de desconto em exames laboratoriais',
                     'Três níveis: Bronze, Prata e Ouro para o seu perfil',
                     'Prioridade no atendimento e na liberação de laudos',
                   ].map((item, idx) => (
                     <li key={idx} className="flex items-start gap-3">
                       <Check className="w-5 h-5 text-santa-gold shrink-0 mt-0.5" />
                       <span className="text-slate-200 font-medium">{item}</span>
                     </li>
                   ))}
                 </ul>

                 <p className="text-sm text-slate-400 mb-8 max-w-md mx-auto md:mx-0">
                   Mais que um cartão de descontos: é a forma inteligente de manter a prevenção em dia e cuidar de toda a família ao longo do ano.
                 </p>

                 <Link to="/viver-mais">
                   <Button variant="primary" className="bg-santa-gold hover:bg-yellow-600 text-santa-dark font-bold border-none shadow-[0_0_20px_rgba(194,162,93,0.3)] hover:shadow-[0_0_30px_rgba(194,162,93,0.5)]">
                     Conheça os Níveis do Cartão
                   </Button>
                 </Link>
              </div>

              <div className="w-full md:w-1/2 relative z-10 flex justify-center md:justify-end">
                 <img
                   src="/cartao-viver-mais.png"
                   alt="Cartão Viver+ nos níveis Bronze, Prata e Ouro"
                   className="w-full max-w-md drop-shadow-2xl animate-float"
                 />
              </div>
           </div>
        </div>
      </section>

    </div>
  );
}
